import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowDown, ArrowUp, History, Radio, Trash2, X } from "lucide-react";
import PageLayout from "../components/PageLayout";
import { EmptyState } from "../components/EmptyState";
import { PosterImage } from "../components/PosterImage";
import { useAuth } from "../contexts/AuthContext";
import { useCatalog } from "../contexts/CatalogContext";
import { db, type FavoriteRow } from "../lib/db";
import { Favorites, syncFavoritesCache } from "../lib/favorites";

export default function FavoritesPage() {
  const { user } = useAuth();
  const { activePlaylist } = useCatalog();
  const [items, setItems] = useState<FavoriteRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);

  const loadFromCache = useCallback(async () => {
    if (!user || !activePlaylist) return;
    const list = await db.favorites
      .where("playlistId")
      .equals(activePlaylist.id)
      .toArray();
    list.sort((a, b) => {
      const ai = a.sortIndex ?? Number.MAX_SAFE_INTEGER;
      const bi = b.sortIndex ?? Number.MAX_SAFE_INTEGER;
      if (ai !== bi) return ai - bi;
      return (a.addedAt ?? 0) - (b.addedAt ?? 0);
    });
    setItems(list);
  }, [user, activePlaylist]);

  const refreshFromServer = useCallback(async () => {
    if (!user || !activePlaylist) return;
    try {
      const fresh = await Favorites.list(activePlaylist.id);
      await syncFavoritesCache(user.id, activePlaylist.id, fresh);
      setError(null);
      await loadFromCache();
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { detail?: string } }; message?: string })
        ?.response?.data?.detail
        || (e as { message?: string })?.message
        || "Failed to load favourites";
      setError(msg);
    }
  }, [user, activePlaylist, loadFromCache]);

  // Cache-first render, then reconcile with the server.
  useEffect(() => {
    loadFromCache();
    refreshFromServer();
  }, [loadFromCache, refreshFromServer]);

  // Re-sync when the page regains focus (cheap; payload is tiny). Catches
  // a fav added on another device or in a different tab.
  useEffect(() => {
    const onFocus = () => refreshFromServer();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [refreshFromServer]);

  const removeFav = async (f: FavoriteRow) => {
    if (!activePlaylist) return;
    const prev = items;
    setItems(items.filter((it) => it.key !== f.key));
    await db.favorites.delete(f.key);
    try {
      await Favorites.remove(
        activePlaylist.id,
        f.streamKind as "live" | "vod" | "series",
        f.streamId,
      );
    } catch {
      setItems(prev);
      await db.favorites.put(f);
    }
  };

  // Reorder via PATCH /order. Server requires the *full* id list, so we
  // build the new order across ALL items (not per-section) and send it in
  // one shot. The UI is sectioned but the underlying storage is one
  // ordered list — moving an item up/down within its section just swaps
  // adjacent items in the global order while their section neighbours
  // remain contiguous (which they are, because the section split happens
  // in render).
  const moveItem = async (key: string, dir: "up" | "down") => {
    if (!activePlaylist) return;
    const idx = items.findIndex((it) => it.key === key);
    if (idx < 0) return;
    // Move only within the SAME section to keep the UX intuitive.
    const me = items[idx];
    const sameSection = (a: FavoriteRow, b: FavoriteRow) => {
      const sk = (r: FavoriteRow) =>
        r.streamKind === "live" || r.streamKind === "catchup" ? "live" : r.streamKind;
      return sk(a) === sk(b);
    };
    let target = -1;
    if (dir === "up") {
      for (let i = idx - 1; i >= 0; i--) {
        if (sameSection(items[i], me)) { target = i; break; }
      }
    } else {
      for (let i = idx + 1; i < items.length; i++) {
        if (sameSection(items[i], me)) { target = i; break; }
      }
    }
    if (target < 0) return;
    const next = items.slice();
    [next[idx], next[target]] = [next[target], next[idx]];
    setItems(next);
    // Optimistic local sortIndex update, then PATCH.
    const ids = next
      .map((it) => it.serverId)
      .filter((v): v is number => typeof v === "number");
    // If any local row hasn't been synced yet (no serverId), refresh
    // from server first to avoid a 400 from PATCH /order.
    if (ids.length !== next.length) {
      await refreshFromServer();
      return;
    }
    try {
      await Favorites.reorder(activePlaylist.id, ids);
      // Refresh sortIndex values in the cache to match server.
      await refreshFromServer();
    } catch {
      // Revert on failure.
      setItems(items);
      setError("Couldn't save the new order. Try again.");
    }
  };

  const sections = useMemo(() => {
    const live: FavoriteRow[] = [];
    const vod: FavoriteRow[] = [];
    const series: FavoriteRow[] = [];
    for (const f of items) {
      // Catchup-kind cache rows (legacy / pre-v3) fold into live for
      // display so we never see them as their own section.
      if (f.streamKind === "live" || f.streamKind === "catchup") live.push(f);
      else if (f.streamKind === "vod") vod.push(f);
      else if (f.streamKind === "series") series.push(f);
    }
    return { live, vod, series };
  }, [items]);

  if (!activePlaylist) {
    return (
      <PageLayout>
        <EmptyState title="Pick a playlist" cta={{ to: "/settings", label: "Open settings" }} />
      </PageLayout>
    );
  }

  const totalCount = sections.live.length + sections.vod.length + sections.series.length;

  return (
    <PageLayout>
      <div className="px-4 md:px-8 py-4">
        <div className="flex items-end justify-between gap-4 mb-6">
          <h1 className="font-display text-[36px] sm:text-[44px] tracking-[-0.5px] text-text-primary">
            Favorites
          </h1>
          {totalCount > 0 && (
            <button
              type="button"
              onClick={() => setEditing((v) => !v)}
              className={`px-3 py-1.5 rounded-pill text-[12px] font-ui font-medium uppercase tracking-[0.04em] transition-fast ${
                editing
                  ? "bg-accent-gold text-canvas"
                  : "border border-muted/40 text-text-secondary hover:border-accent-gold hover:text-accent-gold"
              }`}
            >
              {editing ? "Done" : "Reorder"}
            </button>
          )}
        </div>
        {error && (
          <p className="mb-3 text-[12px] font-ui text-danger">
            Couldn't sync favourites with the server: {error}. Showing the local cache.
          </p>
        )}
        {totalCount === 0 ? (
          <EmptyState
            title="No favorites yet"
            hint="Tap the heart on any channel, movie or series to save it here."
          />
        ) : (
          <div className="space-y-10">
            <Section
              title="Live TV"
              items={sections.live}
              layout="landscape"
              editing={editing}
              onMove={moveItem}
              onRemove={removeFav}
            />
            <Section
              title="Movies"
              items={sections.vod}
              layout="portrait"
              editing={editing}
              onMove={moveItem}
              onRemove={removeFav}
            />
            <Section
              title="Series"
              items={sections.series}
              layout="portrait"
              editing={editing}
              onMove={moveItem}
              onRemove={removeFav}
            />
          </div>
        )}
      </div>
    </PageLayout>
  );
}

function Section({
  title,
  items,
  layout,
  editing,
  onMove,
  onRemove,
}: {
  title: string;
  items: FavoriteRow[];
  layout: "landscape" | "portrait";
  editing: boolean;
  onMove: (key: string, dir: "up" | "down") => void;
  onRemove: (f: FavoriteRow) => void;
}) {
  if (items.length === 0) return null;
  // Mirror the parent listing pages' grid breakpoints exactly so a fav
  // card is the same physical size as it is on /livetv, /movies, /series.
  const gridCls = layout === "landscape"
    ? "grid gap-3 grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5"
    : "grid gap-3 grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-8";
  return (
    <div>
      <h2 className="font-ui font-semibold text-[14px] uppercase tracking-[0.06em] text-text-secondary mb-3">
        {title} <span className="text-text-muted">({items.length})</span>
      </h2>
      <div className={gridCls}>
        {items.map((f, idx) => (
          <FavoriteCard
            key={f.key}
            f={f}
            layout={layout}
            editing={editing}
            isFirst={idx === 0}
            isLast={idx === items.length - 1}
            onMove={onMove}
            onRemove={() => onRemove(f)}
          />
        ))}
      </div>
    </div>
  );
}

function FavoriteCard({
  f,
  layout,
  editing,
  isFirst,
  isLast,
  onMove,
  onRemove,
}: {
  f: FavoriteRow;
  layout: "landscape" | "portrait";
  editing: boolean;
  isFirst: boolean;
  isLast: boolean;
  onMove: (key: string, dir: "up" | "down") => void;
  onRemove: () => void;
}) {
  // Smart row: a live channel with catchup. Show a full-card chooser
  // (NOT a tiny popover — that was hard to hit) so the user can pick
  // "Live" or "Catchup" comfortably.
  const [showChooser, setShowChooser] = useState(false);
  const isCombined = f.streamKind === "live" && f.hasCatchup && f.catchupStreamId;

  const liveHref = `/watch/live/${encodeURIComponent(f.streamId)}`;
  const catchupHref = isCombined
    ? `/catchup/${encodeURIComponent(f.catchupStreamId!)}`
    : null;
  const seriesHref = `/series/${encodeURIComponent(f.streamId)}`;
  const otherHref = f.streamKind === "series"
    ? seriesHref
    : `/watch/${f.streamKind}/${encodeURIComponent(f.streamId)}`;

  const onClick = (e: React.MouseEvent) => {
    if (editing) {
      e.preventDefault();
      return;
    }
    if (!isCombined) return;
    e.preventDefault();
    setShowChooser(true);
  };

  // Mirror the parent listing pages' card aspect: live = 16:9, vod/series = 2:3.
  const aspectCls = layout === "landscape" ? "aspect-video" : "aspect-[2/3]";
  const fitCls = layout === "landscape" ? "object-contain p-3" : "object-cover";

  return (
    <div className="relative group">
      <Link to={isCombined ? liveHref : otherHref} onClick={onClick} className="block">
        <div className={`relative ${aspectCls} rounded-lg overflow-hidden bg-canvas border border-muted/30 transition-fast group-hover:border-accent-gold/60`}>
          <PosterImage
            src={f.poster}
            alt={f.title}
            className="w-full h-full"
            imgClass={`w-full h-full ${fitCls}`}
            orientation={layout === "landscape" ? "row" : "stack"}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-canvas via-transparent opacity-60 group-hover:opacity-90 transition-fast" />
          {f.streamKind === "live" && f.hasCatchup && (
            <div className="absolute top-1.5 left-1.5 flex items-center gap-1 px-1.5 py-0.5 rounded-pill bg-accent-gold/90 text-canvas text-[9px] font-ui font-bold uppercase tracking-[0.06em]">
              Live + Catchup
            </div>
          )}
        </div>
        <p className="mt-1.5 text-[12px] font-ui font-medium text-text-primary truncate">
          {f.title}
        </p>
      </Link>

      {/* Reorder controls — visible only in edit mode. */}
      {editing && (
        <div className="absolute inset-x-0 top-1.5 flex justify-center gap-1 pointer-events-none">
          <button
            type="button"
            onClick={() => onMove(f.key, "up")}
            disabled={isFirst}
            className="pointer-events-auto w-9 h-9 flex items-center justify-center rounded-full bg-canvas/85 backdrop-blur-sm text-text-primary disabled:opacity-30 hover:text-accent-gold transition-fast"
            aria-label="Move up"
          >
            <ArrowUp size={16} />
          </button>
          <button
            type="button"
            onClick={() => onMove(f.key, "down")}
            disabled={isLast}
            className="pointer-events-auto w-9 h-9 flex items-center justify-center rounded-full bg-canvas/85 backdrop-blur-sm text-text-primary disabled:opacity-30 hover:text-accent-gold transition-fast"
            aria-label="Move down"
          >
            <ArrowDown size={16} />
          </button>
        </div>
      )}

      {/* Trash button — top-right, larger touch target so it's hittable on
          mobile without hover. */}
      {!editing && (
        <button
          type="button"
          onClick={onRemove}
          onPointerDown={(e) => e.stopPropagation()}
          className="absolute top-1.5 right-1.5 w-9 h-9 flex items-center justify-center rounded-full bg-canvas/80 backdrop-blur-sm text-text-secondary hover:text-danger opacity-90 hover:opacity-100 transition-fast z-10"
          aria-label="Remove favorite"
        >
          <Trash2 size={16} />
        </button>
      )}

      {/* Live + Catchup chooser — full-card overlay, large hit targets. */}
      {showChooser && isCombined && (
        <div
          className="fixed inset-0 z-50 bg-canvas/80 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setShowChooser(false)}
        >
          <div
            className="glass-panel rounded-2xl p-5 w-full max-w-sm space-y-3"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-1">
              <h3 className="font-display text-[22px] tracking-[-0.5px] text-text-primary">
                {f.title}
              </h3>
              <button
                type="button"
                onClick={() => setShowChooser(false)}
                className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-card-hover text-text-secondary"
                aria-label="Close"
              >
                <X size={16} />
              </button>
            </div>
            <Link
              to={liveHref}
              onClick={() => setShowChooser(false)}
              className="flex items-center gap-3 p-4 rounded-xl bg-surface hover:bg-card-hover border border-muted/30 hover:border-accent-gold/60 transition-fast"
            >
              <span className="w-12 h-12 rounded-full bg-danger/20 text-danger flex items-center justify-center flex-shrink-0">
                <Radio size={22} />
              </span>
              <span className="flex-1 min-w-0">
                <span className="block text-[15px] font-ui font-medium text-text-primary">Watch live</span>
                <span className="block text-[12px] font-ui text-text-secondary">Stream the channel right now</span>
              </span>
            </Link>
            <Link
              to={catchupHref!}
              onClick={() => setShowChooser(false)}
              className="flex items-center gap-3 p-4 rounded-xl bg-surface hover:bg-card-hover border border-muted/30 hover:border-accent-gold/60 transition-fast"
            >
              <span className="w-12 h-12 rounded-full bg-accent-gold/20 text-accent-gold flex items-center justify-center flex-shrink-0">
                <History size={22} />
              </span>
              <span className="flex-1 min-w-0">
                <span className="block text-[15px] font-ui font-medium text-text-primary">Catch up</span>
                <span className="block text-[12px] font-ui text-text-secondary">Pick a programme that already aired</span>
              </span>
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
